import { useState, useMemo } from "react";
import { Scale, Ruler, Activity } from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import type { MetricEntry } from "./MetricsSummaryCards";

interface Props {
  entries: MetricEntry[];
}

const chartTabs = [
  { key: "weight" as const, label: "Peso", icon: Scale, unit: "kg" },
  { key: "waist" as const, label: "Cintura", icon: Ruler, unit: "cm" },
  { key: "hip" as const, label: "Quadril", icon: Activity, unit: "cm" },
] as const;

type ChartKey = typeof chartTabs[number]["key"];

const MetricsChart = ({ entries }: Props) => {
  const [activeChart, setActiveChart] = useState<ChartKey>("weight");

  const chartData = useMemo(() => {
    return entries.map(e => ({
      date: new Date(e.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
      weight: e.weight,
      waist: e.waist,
      hip: e.hip,
    }));
  }, [entries]);

  if (entries.length < 2) return null;

  return (
    <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
      <div className="flex gap-2 mb-4">
        {chartTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveChart(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeChart === tab.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} domain={["auto", "auto"]} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "12px",
              fontSize: 12,
            }}
          />
          <Area
            type="monotone"
            dataKey={activeChart}
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#colorMetric)"
            dot={{ fill: "hsl(var(--primary))", r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MetricsChart;
